<script setup lang="ts">
import { computed } from 'vue';
import { useTimesheetStore } from '@/stores/timesheets';
import { useTimesheetSetupStore } from '@/stores/timesheetSetup';
import { useKioskConfigStore } from '@/stores/kioskConfig';
import { useAreaStore } from '@/stores/areas';
import { getTranslations } from '@/lib/util/i18n';
import { CoButton } from '@deputy/copilot2';
import LoadingSpinner from './LoadingSpinner.vue';
import { displayFromISO } from '@/lib/util/dateTime';
import ShiftQuestionForm from './ShiftQuestionForm.vue';
import { DateTime } from 'luxon';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'shift-end-information',
});

const timesheets = useTimesheetStore();
const kioskConfig = useKioskConfigStore();
const areas = useAreaStore();
const timesheetSetup = useTimesheetSetupStore();

timesheets.loadForActiveUser();

const props = defineProps<{
  endTimeUserOverride?: number;
  startTimeUserOverride?: number;
}>();

const endTime = computed(
  () => props.endTimeUserOverride || DateTime.now().toMillis()
);
const startTime = computed(
  () =>
    props.startTimeUserOverride ||
    DateTime.fromISO(timesheets.getStartShiftTime()).toMillis()
);

const timesheetBlock = kioskConfig.timesheetClosestBlock;
const totalMinutes = computed(() =>
  timesheets.getElapsedRemainderMinutes(endTime.value, startTime.value)
);
const totalHours = computed(() =>
  timesheets.getElapsedIntegerHours(endTime.value, startTime.value)
);

function timeTranslation() {
  const minutesTranslation =
    totalMinutes.value != 1 ? t('timer-minutes-plural') : t('timer-minutes');
  const hoursTranslation =
    totalHours.value != 1 ? t('timer-hours-plural') : t('timer-hours');
  return { hours: hoursTranslation, minutes: minutesTranslation };
}

function handleShiftQuestionsSubmission(
  _: number,
  shiftQuestionValues: Record<string, string>
) {
  emit('showEndShiftConfirmation', shiftQuestionValues);
}

const emit = defineEmits<{
  (e: 'closeWithoutReload'): void;
  (e: 'showEditShift'): void;
  (
    e: 'showEndShiftConfirmation',
    shiftQuestionValues?: Record<string, string>
  ): void;
}>();
</script>

<template>
  <LoadingSpinner v-if="timesheets.isLoading"></LoadingSpinner>
  <div v-else class="shift-end-information">
    <div v-if="timesheets.canEndShift()">
      <div>
        <div class="__shift-header">
          <span v-if="totalHours > 0"
            >{{ totalHours }}{{ timeTranslation().hours }}</span
          >
          <span>{{ totalMinutes }}{{ timeTranslation().minutes }}</span>
        </div>

        <a
          :class="{
            __readonly:
              !timesheetSetup.canModifyTimesheetOnEnd || timesheets.isOnBreak,
          }"
          @click="emit('showEditShift')"
        >
          <div class="__shift-subheader">
            {{
              t('shift-time', {
                startTime: displayFromISO(
                  DateTime.fromMillis(startTime).toISO()
                ),
                endTime: displayFromISO(DateTime.fromMillis(endTime).toISO()),
              })
            }}
          </div>
        </a>
      </div>
      <div class="__shift-question-form">
        <ShiftQuestionForm
          :cancel-button-text="t('cancel')"
          :submit-button-text="t('end-shift')"
          :comment-box="true"
          @cancel="emit('closeWithoutReload')"
          @submit="handleShiftQuestionsSubmission"
        />
      </div>
    </div>
    <div v-else>
      <div>
        <img src="../assets/images/short-shift.png" />
        <div class="__title">{{ t('short-shift-title') }}</div>
      </div>
      <div class="__short-shift-warning">
        {{
          t('short-shift-warning', {
            companyName: areas.getAreaById(
              timesheets.activeTimesheetOrThrow.OperationalUnit
            )?.CompanyName,
            timesheetBlock: timesheetBlock,
          })
        }}
      </div>
      <div class="__actions">
        <CoButton size="large" @click="emit('closeWithoutReload')">{{
          t('cancel')
        }}</CoButton>
        <CoButton
          size="large"
          type="primary"
          @click="emit('showEndShiftConfirmation')"
          >{{ t('delete-timesheet') }}</CoButton
        >
      </div>
    </div>
  </div>
</template>

<style scoped>
.shift-end-information {
  width: 100%;
}
.__shift-header {
  margin-bottom: 8px;
  font-size: 22px;
  line-height: 32px;
  color: var(--co-text-primary);
}
.__shift-header span {
  font-weight: 600;
}
.__shift-subheader {
  font-size: 18px;
  line-height: 24px;
  color: var(--co-text-secondary);
  margin-bottom: 32px;
}
.__readonly {
  pointer-events: none;
}
.co-textarea {
  margin-bottom: 32px;
  font-size: 14px;
}

img {
  width: 100px;
  height: 100px;
  margin-bottom: 24px;
}
.__title {
  font-weight: 500;
  font-size: 30px;
  line-height: 30px;
  color: var(--co-text-primary);
  margin-bottom: 16px;
}
.__short-shift-warning {
  margin-bottom: 32px;
  line-height: 24px;
  font-size: 17px;
  line-height: 22px;
}
.__actions {
  width: 100%;
  display: flex;
}
.co-button {
  width: 100%;
}

.__shift-question-form {
  display: flex;
  flex-direction: column;
  text-align: justify;
}
</style>
